import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import {
  StyledCardRow,
  StyledCardDivider,
  StyledSummaryRow,
  StyledSummaryDivider
} from '../SubscriptionCancelPage.styles';
import { formatDate, formatPrice } from '../utils';

const SUMMARY_ROW_2_SX = {
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 0
};

// Shared subscription summary for cancel flow: used on confirm step (variant="card") and confirmation step (variant="summary").
// titleMessageId (product-catalog) preferred; fallback to summary or service.
export function SubscriptionSummaryContent({
  period,
  summary,
  service,
  titleMessageId,
  price,
  termStartDate,
  inTrial,
  trialDays,
  variant = 'card'
}) {
  const { formatMessage } = useIntl();

  const isSummary = variant === 'summary';
  const Row = isSummary ? StyledSummaryRow : StyledCardRow;
  const Divider = isSummary ? StyledSummaryDivider : StyledCardDivider;
  const row2Sx = isSummary ? SUMMARY_ROW_2_SX : undefined;

  const periodLabel = period === 'yearly'
    ? formatMessage({ id: 'dashboard.subscriptionCancel.subscription.period.annual' })
    : formatMessage({ id: 'dashboard.subscriptionCancel.subscription.period.monthly' });
  const intervalLabel = period === 'yearly'
    ? formatMessage({ id: 'dashboard.subscriptionCancel.subscription.interval.year' })
    : formatMessage({ id: 'dashboard.subscriptionCancel.subscription.interval.month' });
  const displayName = titleMessageId
    ? formatMessage({ id: titleMessageId })
    : (summary || service);

  return (
    <>
      <Row>
        <Box flex={1}>
          <Text size="sm" weight="bold">{displayName}</Text>
          <Text size="xs" color="text.secondary">
            {periodLabel}
            {' '}
            {formatMessage({ id: 'dashboard.subscriptionCancel.subscription.label' })}
          </Text>
        </Box>
        <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
          <Text size="sm">
            <Box component="span" fontWeight="bold">{formatPrice(price)}</Box>
            <Box component="span" sx={{ fontWeight: 400 }}>
              {' '}
              /
              {' '}
              {intervalLabel}
            </Box>
          </Text>
        </Box>
      </Row>

      <Divider />

      <Row sx={row2Sx}>
        <Stack gap={0.5}>
          <Text size="xs" color="text.secondary">
            {formatMessage({ id: 'dashboard.subscriptionCancel.subscription.startDate' })}
          </Text>
          <Text size="sm" weight="bold">{formatDate(termStartDate)}</Text>
          {inTrial && trialDays > 0 && (
            <Text size="xs" color="text.secondary">
              {formatMessage(
                { id: 'dashboard.subscriptionCancel.trial.daysWarning' },
                { days: trialDays }
              )}
            </Text>
          )}
        </Stack>
      </Row>
    </>
  );
}

SubscriptionSummaryContent.propTypes = {
  period: PropTypes.string.isRequired,
  summary: PropTypes.string,
  service: PropTypes.string,
  titleMessageId: PropTypes.string,
  price: PropTypes.number.isRequired,
  termStartDate: PropTypes.string,
  inTrial: PropTypes.bool,
  trialDays: PropTypes.number,
  variant: PropTypes.oneOf(['card', 'summary'])
};
